import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { fetchDetection } from '../api';

// クラスIDと牌のマッピング
const CLASS_NAMES = [
  '1m', '2m', '3m', '4m', '5m', '6m', '7m', '8m', '9m',
  '1p', '2p', '3p', '4p', '5p', '6p', '7p', '8p', '9p',
  '1s', '2s', '3s', '4s', '5s', '6s', '7s', '8s', '9s',
  'r5m', 'r5p', 'r5s',
  'ton', 'nan', 'sha', 'pe', 'haku', 'hatu', 'chun',
];

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const HaiDetectionPage = ({ setPageTitle }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [detectionResult, setDetectionResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);

  // AppBarのタイトルを設定
  useEffect(() => {
    setPageTitle('牌検出');
  }, [setPageTitle]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    // ファイルタイプの検証
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setError('JPEGまたはPNG形式の画像を選択してください。');
      return;
    }

    // ファイルサイズの検証 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('ファイルサイズは5MB以下にしてください。');
      return;
    }

    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    setDetectionResult(null);
    setError('');
  };

  const handleDetect = useCallback(async () => {
    if (!selectedFile) {
      setError('画像が選択されていません。');
      return;
    }

    setIsLoading(true);
    setError('');
    setDetectionResult(null);

    try {
      const result = await fetchDetection(selectedFile);
      setDetectionResult(result);
    } catch (err) {
      setError(err.response?.data?.detail || '検出中にエラーが発生しました。');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile]);

  const handleClear = () => {
    setSelectedFile(null);
    setPreview(null);
    setDetectionResult(null);
    setError('');
    // inputの値をリセットするためにDOMを直接操作
    const fileInput = document.getElementById('hai-image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // canvasに画像と検出結果の矩形を描画する
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !preview) return;

    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = preview;

    img.onload = () => {
      // --- Canvasの内部解像度を元画像に合わせ、高画質ズームを可能にする ---
      // 1. Canvasの内部解像度（描画領域）を、元画像のサイズに設定する
      //    CSSで表示サイズが100%幅に制約されているため、レイアウトは崩れない
      canvas.width = img.width;
      canvas.height = img.height;

      // 2. 元画像をそのままの解像度で描画する
      ctx.drawImage(img, 0, 0);

      // 検出結果があれば矩形を描画
      if (detectionResult) {
        // --- 動的なスタイル計算 ---
        // 描画要素のサイズを、画像の解像度に応じて動的に変更する
        const lineWidth = Math.max(1, img.width / 600); // 枠線の太さ (最小1px)
        const fontSize = Math.max(10, img.width / 80);  // フォントサイズ (最小10px)
        const labelPadding = Math.max(2, img.width / 250); // ラベル内の余白 (最小2px)
        const labelHeight = fontSize + (labelPadding * 2); // ラベルの高さ

        detectionResult.forEach(box => {
          const { x1, y1, x2, y2, confidence, classId } = box;

          const label = CLASS_NAMES[classId] || `ID:${classId}`;
          // ラベルのテキストをより短くするために、括弧とパーセント記号を削除
          const text = `${label} ${(confidence * 100).toFixed(0)}`;

          // 矩形の描画
          ctx.strokeStyle = '#f50057'; // primary color
          ctx.lineWidth = lineWidth;
          ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

          // ラベルの背景
          ctx.fillStyle = 'rgba(245, 0, 87, 0.75)'; // primary color with 75% opacity
          ctx.font = `${fontSize}px Arial`;
          const textMetrics = ctx.measureText(text);
          const labelY = y1 > labelHeight ? y1 - labelHeight : y1;
          ctx.fillRect(x1, labelY, textMetrics.width + (labelPadding * 2), labelHeight);

          // ラベルのテキスト
          ctx.fillStyle = 'white';
          ctx.fillText(text, x1 + labelPadding, labelY + fontSize + labelPadding);
        });
      }
    };
    img.onerror = () => {
      setError("画像の描画に失敗しました。");
    }
  }, [preview, detectionResult]);

  return (
    <>
      {/* 画面サイズに応じてコンポーネント間の隙間を調整する */}
      <Grid container spacing={{ xs: 2, md: 4 }}>
        <Grid item xs={12} md={5} >
          <Paper sx={{ p: 3 }}>
            <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                画像ファイルを選択
                <VisuallyHiddenInput
                  type="file"
                  id="hai-image-upload"
                  onChange={handleFileChange}
                  accept="image/png, image/jpeg"
                />
              </Button>
              {selectedFile && (
              <Box sx={{ mt: 1.5, pl: 0.5, display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexShrink: 0, mr: 0.5 }}
                >
                  選択中のファイル:
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    flexGrow: 1, // 残り幅を全部使って省略処理
                  }}
                  title={selectedFile.name}
                >
                  {selectedFile.name}
                </Typography>
              </Box>
              )}
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDetect}
                disabled={!selectedFile || isLoading}
                sx={{ flexGrow: 1 }}
              >
                {isLoading ? <CircularProgress size={24} /> : '牌を検出'}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClear}
                disabled={isLoading}
              >
                クリア
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* 右側: 結果表示エリア */}
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" gutterBottom>
            検出結果
          </Typography>
          <Box sx={{
            p: 2,
            border: '2px solid #ccc',
            borderRadius: '5px',
            minHeight: '300px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
          }}>
            {error && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{error}</Alert>}
            {isLoading && <CircularProgress sx={{ mt: 4 }} />}

            {/* プレビューまたは結果がない場合のメッセージ */}
            {!isLoading && !preview && !error && (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                画像をアップロードして「牌を検出」ボタンを押してください。
              </Typography>
            )}

            {/* canvasを表示 */}
            {preview && (
              <Box sx={{ width: '100%', mt: 2, display: 'flex', justifyContent: 'center' }}>
                <canvas
                  ref={canvasRef}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default HaiDetectionPage;
